import { useEffect, useState } from 'react'

export type DynamicTimeMode = 'COUNTDOWN' | 'FIXED'

function value(
  style: Record<string, unknown> | undefined,
  key: string,
  fallback: string
) {
  return typeof style?.[key] === 'string' ? style[key] : fallback
}

function enabled(
  style: Record<string, unknown> | undefined,
  key: string,
  fallback: boolean
) {
  return typeof style?.[key] === 'boolean' ? style[key] : fallback
}

function targetParts(targetTime: string) {
  const match = /^(\d{2}):(\d{2})$/.exec(targetTime)
  const hour = Math.min(23, Math.max(0, Number(match?.[1] ?? 13)))
  const minute = Math.min(59, Math.max(0, Number(match?.[2] ?? 15)))
  return { hour, minute }
}

function localClockParts(now: Date, timeZone: string) {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now)
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value ?? 0)
    return { hour: get('hour'), minute: get('minute'), second: get('second') }
  } catch {
    return {
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds(),
    }
  }
}

function localizeDigits(text: string, locale: string) {
  if (!locale.toLowerCase().startsWith('bn')) return text
  const digits = '০১২৩৪৫৬৭৮৯'
  return text.replace(/\d/g, (digit) => digits[Number(digit)] ?? digit)
}

function padded(value: number) {
  return String(value).padStart(2, '0')
}

export function formatDynamicTime(
  style: Record<string, unknown> | undefined,
  now = new Date()
) {
  const mode = value(style, 'timerMode', 'COUNTDOWN') as DynamicTimeMode
  const targetTime = value(style, 'targetTime', '13:15')
  const timeZone = value(style, 'timezone', 'Asia/Dhaka')
  const locale = value(style, 'locale', 'bn-BD')
  const hourCycle = value(style, 'hourCycle', 'H12')
  const showSeconds = enabled(style, 'showSeconds', mode === 'COUNTDOWN')
  const target = targetParts(targetTime)

  if (mode === 'FIXED') {
    let hour = target.hour
    let suffix = ''
    if (hourCycle === 'H12') {
      suffix = hour >= 12 ? ' PM' : ' AM'
      hour = hour % 12 || 12
    }
    const output = `${padded(hour)}:${padded(target.minute)}${
      showSeconds ? ':00' : ''
    }${suffix}`
    return localizeDigits(output, locale)
  }

  const current = localClockParts(now, timeZone)
  const targetSeconds = target.hour * 3600 + target.minute * 60
  const currentSeconds =
    current.hour * 3600 + current.minute * 60 + current.second
  let remaining = targetSeconds - currentSeconds
  if (remaining < 0) remaining += 24 * 3600
  const hours = Math.floor(remaining / 3600)
  const minutes = Math.floor((remaining % 3600) / 60)
  const seconds = remaining % 60
  const output = showSeconds
    ? `${padded(hours)}:${padded(minutes)}:${padded(seconds)}`
    : `${padded(hours)}:${padded(minutes)}`
  return localizeDigits(output, locale)
}

export function DynamicTimePreview({
  style,
}: {
  style: Record<string, unknown> | undefined
}) {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 1_000)
    return () => window.clearInterval(interval)
  }, [])

  return <>{formatDynamicTime(style, now)}</>
}
